import { useState } from 'react';
import { 
  calculateCompoundInterest, 
  calculateScenarios, 
  calculateTimeToTarget 
} from '../utils/calculations';

const useCalculator = () => {
  const [results, setResults] = useState({
    data: [],
    chartData: [],
    summary: {}
  });
  const [scenarios, setScenarios] = useState([]);
  const [milestones, setMilestones] = useState([]);
  
  /**
   * Calcula resultados básicos da calculadora
   * @param {Object} formData - Dados do formulário
   * @returns {Array} Resultados calculados
   */
  const calculateResults = (formData) => {
    const { initialCapital, monthlyContribution, interestRate, period, taxRate } = formData;
    
    // Converter taxas para formato decimal
    const monthlyRate = interestRate / 100;
    const taxRateDecimal = taxRate / 100;
    
    const calculatedData = calculateCompoundInterest(
      initialCapital,
      monthlyContribution,
      monthlyRate,
      period,
      taxRateDecimal
    );
    
    // Processar os dados para o gráfico
    const chartData = calculatedData.map(item => ({
      month: item.month,
      totalAmount: item.totalAmount,
      investedAmount: item.totalCapital,
      interestAmount: item.totalInterest
    }));
    
    // Pegar o último item para o resumo
    const lastItem = calculatedData[calculatedData.length - 1];
    const summary = {
      finalAmount: lastItem.totalAmount,
      investedAmount: lastItem.totalCapital,
      interestAmount: lastItem.totalInterest,
      returnRate: lastItem.returnRate,
      taxAmount: lastItem.tax,
      netAmount: lastItem.netAmount
    };
    
    const resultsObject = {
      data: calculatedData,
      chartData: chartData,
      summary: summary
    };
    
    setResults(resultsObject);
    return resultsObject;
  };
  
  /**
   * Calcula diferentes cenários de investimento
   * @param {Object} formData - Dados do formulário
   * @param {Array} scenariosData - Dados dos cenários
   * @returns {Array} Resultados dos cenários
   */
  const calculateInvestmentScenarios = (formData, scenariosData) => {
    const { initialCapital, monthlyContribution, period } = formData;
    
    const calculatedScenarios = calculateScenarios(
      initialCapital,
      monthlyContribution,
      period,
      scenariosData
    );
    
    setScenarios(calculatedScenarios);
    return calculatedScenarios;
  };
  
  /**
   * Calcula o tempo para atingir marcos financeiros
   * @param {Object} formData - Dados do formulário
   * @param {Array} targets - Valores-alvo
   * @returns {Object} Resultados dos marcos
   */
  const calculateMilestones = (formData, targets) => {
    const { initialCapital, monthlyContribution, interestRate } = formData;
    const monthlyRate = interestRate / 100;
    
    // Filtra apenas alvos habilitados
    const enabledTargets = targets.filter(target => target.enabled);
    
    // Calcula o tempo para cada alvo
    const milestonesResults = enabledTargets.map(target => {
      const result = calculateTimeToTarget(
        initialCapital,
        monthlyContribution,
        monthlyRate,
        target.value
      );
      
      return {
        name: target.name,
        targetValue: target.value,
        timeInMonths: result.months,
        timeInYears: result.years,
        finalAmount: result.amount,
        targetReached: result.targetReached
      };
    });
    
    // Ordena por tempo para atingir
    milestonesResults.sort((a, b) => a.timeInMonths - b.timeInMonths);
    
    // Encontra o tempo máximo (para o gráfico)
    const maxMonths = milestonesResults.length > 0 
      ? Math.max(...milestonesResults.map(m => m.timeInMonths)) 
      : 120;
    
    // Gera dados para o gráfico
    const chartData = [];
    let amount = initialCapital;
    
    for (let month = 0; month <= maxMonths; month++) {
      // Calcula o valor acumulado em cada mês
      if (month > 0) {
        // Juros do mês
        const interest = amount * monthlyRate;
        // Acrescenta o aporte mensal e os juros
        amount = amount + monthlyContribution + interest;
      }
      
      // Adiciona o ponto no gráfico
      chartData.push({
        month,
        amount
      });
      
      // Marca os alvos atingidos neste mês
      const targetsInMonth = milestonesResults.filter(m => m.timeInMonths === month);
      if (targetsInMonth.length > 0) {
        const lastIndex = chartData.length - 1;
        chartData[lastIndex].milestone = targetsInMonth.map(t => ({
          name: t.name,
          value: t.targetValue
        }));
      }
    }
    
    const result = {
      milestones: milestonesResults,
      chartData
    };
    
    setMilestones(result);
    return result;
  };
  
  return {
    results,
    scenarios,
    milestones,
    calculateResults,
    calculateInvestmentScenarios,
    calculateMilestones
  };
};

export default useCalculator;